/**
 * Tenant Settings Hooks (React Query)
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { tenantsApi } from "@/api/tenants";
import type { UpdateTenantRequest } from "@/types/tenant";
import { toast } from "react-hot-toast";
import { useAuthStore } from "@/store/authStore";

/**
 * Get current tenant
 * @param skip - If true, skip fetching (useful for super_users who don't have a tenant)
 */
export const useCurrentTenant = (skip = false) => {
  return useQuery({
    queryKey: ["tenant", "current"],
    queryFn: async () => {
      const response = await tenantsApi.getCurrent();
      return response.data;
    },
    enabled: !skip,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Get public tenant info (no authentication required)
 * For use on the public application form
 */
export const usePublicTenant = (tenantId: string) => {
  return useQuery({
    queryKey: ["public-tenant", tenantId],
    queryFn: async () => {
      const response = await tenantsApi.getPublic(tenantId);
      return response.data;
    },
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Get public tenant branding by slug (no authentication required)
 * For use on login, forgot password, and other public pages
 */
export const usePublicBranding = (slug: string | null) => {
  return useQuery({
    queryKey: ["public-branding", slug],
    queryFn: async () => {
      const response = await tenantsApi.getPublicBranding(slug!);
      return response.data;
    },
    enabled: !!slug,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Get tenant by ID
 */
export const useTenant = (tenantId: string) => {
  return useQuery({
    queryKey: ["tenant", tenantId],
    queryFn: async () => {
      const response = await tenantsApi.get(tenantId);
      return response.data;
    },
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Get tenant's Stripe publishable key (for loading Stripe.js dynamically)
 * Used by MakePaymentPage which needs the key before creating a PaymentIntent.
 */
export const useTenantStripePublishableKey = () => {
  const { isAuthenticated } = useAuthStore();
  return useQuery({
    queryKey: ["stripe-publishable-key"],
    queryFn: async () => {
      const response = await tenantsApi.getStripePublishableKey();
      return response.data.publishable_key;
    },
    enabled: isAuthenticated,
    staleTime: 30 * 60 * 1000, // 30 minutes
  });
};

/**
 * Update tenant settings mutation
 */
export const useUpdateTenant = (tenantId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateTenantRequest) => tenantsApi.update(tenantId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tenant"] });
      toast.success("School settings updated successfully");
    },
    onError: (error: unknown) => {
      const axiosError = error as { response?: { data?: { message?: string } } };
      const message =
        axiosError.response?.data?.message || "Failed to update school settings";
      toast.error(message);
    },
  });
};
